import { z } from 'zod';
import {
  creditNoteReasons,
  creditNoteStatuses,
  invoiceStatuses,
  languages,
  paymentMethods,
  paymentProviders,
  type CreditNoteReason,
  type CreditNoteStatus,
  type InvoiceStatus,
  type PaymentMethod,
  type PaymentProvider,
  type PaymentStatus,
  type VatMode,
} from '../enums.js';
import { optionalText, trimmed } from './_fields.js';
import {
  basisPointsSchema,
  centsSchema,
  lineQuantitySchema,
  nonNegativeCentsSchema,
  positiveCentsSchema,
} from './_billing-fields.js';
import { paginationFields } from './_pagination.js';
import { dateOnlySchema } from './patients.js';

/**
 * Contrat réseau des factures, de leurs lignes, du registre des paiements et des avoirs.
 *
 * **Le client ne fixe aucun total.** Les champs de montant calculés sont acceptés — le dialogue de
 * création les affiche et les renvoie — mais le serveur les ignore et recalcule tout avec
 * `@osteopro/invoicing`. Un schéma strict qui les refuserait obligerait le front à démonter son
 * propre modèle ; les accepter en les ignorant est la seule façon de garantir qu'un total faux
 * n'a aucun effet.
 */

/* -------------------------------------------------------------------------- */
/* Entrées                                                                     */
/* -------------------------------------------------------------------------- */

export const invoiceIdParamSchema = z.object({ id: z.uuid() }).strict();
export const invoicePaymentParamSchema = z.object({ id: z.uuid(), paymentId: z.uuid() }).strict();
export const creditNoteIdParamSchema = z.object({ id: z.uuid() }).strict();

/**
 * Mentions qui manquent à une facture au moment de l'émettre. **Rien n'est bloqué** (décision de
 * Nicolas, 11 octobre 2026) : sans confirmation, `POST /api/invoices/:id/issue` répond
 * `409 invoice_legal_mentions_missing` avec `details.missing`, aucun numéro n'est tiré ; avec
 * `confirmMissingMentions: true`, la facture s'émet telle quelle.
 */
export const invoiceIssueWarningCodes = [
  'missing_organization_address',
  'missing_authorization_number',
  'missing_organization_vat_number',
  'missing_customer_vat_number',
] as const;
export type InvoiceIssueWarningCode = (typeof invoiceIssueWarningCodes)[number];

/** Corps facultatif de l'émission. Absent : aucune confirmation donnée. */
export const invoiceIssueSchema = z
  .object({ confirmMissingMentions: z.boolean().optional() })
  .strict();
export type InvoiceIssueInput = z.infer<typeof invoiceIssueSchema>;

/** `details` du 409 `invoice_legal_mentions_missing`. */
export interface InvoiceLegalMentionsMissingDetails {
  missing: InvoiceIssueWarningCode[];
}

/**
 * Ligne saisie. Ni `vatRateBps` ni `totalCents` ne sont lus : le taux vient du régime de TVA du
 * cabinet et le total du calcul ligne par ligne de `computeInvoiceTotals`.
 */
export const invoiceLineInputSchema = z
  .object({
    label: trimmed(300),
    quantity: lineQuantitySchema,
    unitPriceCents: nonNegativeCentsSchema,
    consultationTypeId: z.uuid().nullable().optional(),
    /**
     * Classification hors ostéopathie de la ligne. **Ignorée dès qu'un type de consultation est
     * donné** : le serveur recopie alors celle du catalogue, seule source de la classification.
     * Elle ne sert qu'aux lignes libres, qui n'ont pas de type derrière elles.
     */
    outsideOsteopathy: z.boolean().optional(),
    /** Accepté et ignoré : recalculé par le serveur. */
    vatRateBps: basisPointsSchema.optional(),
    /** Accepté et ignoré : recalculé par le serveur. */
    totalCents: centsSchema.optional(),
  })
  .strict();
export type InvoiceLineInput = z.infer<typeof invoiceLineInputSchema>;

const invoiceBodyFields = {
  encounterId: z.uuid().nullable().optional(),
  locationId: z.uuid().nullable().optional(),
  issueDate: dateOnlySchema,
  notes: optionalText(4_000).optional(),
  lines: z.array(invoiceLineInputSchema).min(1).max(100),
  /** Acceptés et ignorés : recalculés par le serveur. */
  subtotalCents: centsSchema.optional(),
  vatCents: centsSchema.optional(),
  totalCents: centsSchema.optional(),
};

export const invoiceCreateSchema = z
  .object({
    patientId: z.uuid(),
    practitionerMemberId: z.uuid(),
    ...invoiceBodyFields,
  })
  .strict();
export type InvoiceCreateInput = z.infer<typeof invoiceCreateSchema>;

/** Édition d'un brouillon. Le patient ne change pas : une facture destinée à un autre se recrée. */
export const invoiceUpdateSchema = z
  .object({ practitionerMemberId: z.uuid(), ...invoiceBodyFields })
  .partial()
  .strict();
export type InvoiceUpdateInput = z.infer<typeof invoiceUpdateSchema>;

/**
 * Un ou plusieurs statuts, séparés par des virgules : `?status=issued,partially_paid,paid`.
 *
 * L'éligibilité à un avoir couvre trois statuts à elle seule. Avec un statut unique, le front
 * n'avait d'autre choix que d'élargir la page et de filtrer lui-même, en avertissant d'une
 * troncature — honnête, mais c'est au serveur de filtrer ce que le serveur sait filtrer.
 * Une valeur seule reste acceptée : elle vaut une liste d'un élément.
 */
export const invoiceStatusFilterSchema = z
  .string()
  .transform((value) => value.split(',').map((part) => part.trim()))
  .pipe(z.array(z.enum(invoiceStatuses)).min(1).max(invoiceStatuses.length));

export const invoiceListQuerySchema = z
  .object({
    status: invoiceStatusFilterSchema.optional(),
    patientId: z.uuid().optional(),
    /**
     * Pièces rattachées à une consultation — la section « Facturation » de la séance en cours.
     * Comme le filtre par dossier, il **borne naturellement le résultat** : une consultation porte
     * une poignée de pièces, là où une fenêtre de dates ouverte en porte des milliers.
     */
    encounterId: z.uuid().optional(),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    ...paginationFields,
  })
  .strict();
export type InvoiceListQuery = z.infer<typeof invoiceListQuerySchema>;

/**
 * Encaissement. Le montant est plafonné par le serveur, pas par le formulaire : au reste dû plus
 * les intérêts de retard encore dus, répartis par `allocatePayment` de `@osteopro/invoicing`.
 */
export const paymentCreateSchema = z
  .object({
    method: z.enum(paymentMethods),
    amountCents: positiveCentsSchema,
    receivedAt: z.iso.datetime().optional(),
    reference: optionalText(140).optional(),
    provider: z.enum(paymentProviders).nullable().optional(),
    providerRef: optionalText(140).optional(),
  })
  .strict();
export type PaymentCreateInput = z.infer<typeof paymentCreateSchema>;

/**
 * Avoir. Sans `lines`, le serveur en compose une seule à partir de `amountCents`, qui est un
 * montant **toutes taxes comprises** : c'est ce que le dialogue de la V1 saisissait.
 */
export const creditNoteCreateSchema = z
  .object({
    reason: z.enum(creditNoteReasons),
    issueDate: dateOnlySchema,
    amountCents: positiveCentsSchema.optional(),
    lines: z.array(invoiceLineInputSchema).min(1).max(100).optional(),
    notes: optionalText(4_000).optional(),
  })
  .strict()
  .refine(
    (value) => value.amountCents !== undefined || value.lines !== undefined,
    'Montant ou lignes requis',
  );
export type CreditNoteCreateInput = z.infer<typeof creditNoteCreateSchema>;

/**
 * Émission d'un avoir. Corps absent ou vide : on n'ouvre pas la facturation.
 *
 * `reopenBilling` demande de rendre à nouveau facturables les actes liés à cette facture.
 * Le serveur n'ouvre que si cet avoir annule leur solde facturé ; un avoir partiel est ignoré
 * sur ce point, l'avoir lui-même est émis.
 */
export const creditNoteIssueSchema = z
  .object({
    reopenBilling: z.boolean().optional(),
  })
  .strict();
export type CreditNoteIssueInput = z.infer<typeof creditNoteIssueSchema>;

export const creditNoteListQuerySchema = z
  .object({
    invoiceId: z.uuid().optional(),
    /** La consultation de la **facture** avoirée : un avoir n'en porte pas lui-même. */
    encounterId: z.uuid().optional(),
    status: z.enum(creditNoteStatuses).optional(),
    ...paginationFields,
  })
  .strict();
export type CreditNoteListQuery = z.infer<typeof creditNoteListQuerySchema>;

/* -------------------------------------------------------------------------- */
/* Sorties                                                                     */
/* -------------------------------------------------------------------------- */

export interface InvoiceLineDto {
  id: string;
  position: number;
  label: string;
  quantity: number;
  unitPriceCents: number;
  vatRateBps: number;
  totalCents: number;
  consultationTypeId: string | null;
  outsideOsteopathy: boolean;
}

export interface PaymentDto {
  id: string;
  invoiceId: string;
  method: PaymentMethod;
  /** Ce qui a été reçu. La part facture est `amountCents − interestCents`. */
  amountCents: number;
  /** Part imputée aux intérêts de retard `lateInterestId` ; nulle le plus souvent. */
  interestCents: number;
  lateInterestId: string | null;
  receivedAt: string;
  reference: string | null;
  provider: PaymentProvider | null;
  providerRef: string | null;
  status: PaymentStatus;
  recordedBy: string | null;
  createdAt: string;
}

/**
 * Instantané des mentions légales figé à l'émission, produit par `buildLegalMentions` de
 * `@osteopro/invoicing`. Les collections sont en lecture seule : ce bloc est une pièce figée, il se
 * lit et s'imprime, il ne se modifie pas.
 */
/**
 * Client **tel qu'il était à l'émission**, figé sur la facture comme le praticien.
 *
 * Écrit à la main : ce n'est pas un schéma dérivé de la table. Un brouillon n'en a pas.
 * Une facture déjà émise avant cette colonne non plus : on ne grave pas l'identité du jour
 * sur une pièce déjà comptabilisée. Aucun champ clinique, aucun secret, aucun montant.
 */
export const patientSnapshotSchema = z
  .object({
    firstName: trimmed(100),
    lastName: trimmed(100),
    companyName: optionalText(160),
    vatNumber: optionalText(30),
    /** Rue telle qu'imprimée : numéro et voie sur une seule ligne. */
    addressLine1: optionalText(200),
    addressLine2: optionalText(160),
    postalCode: optionalText(20),
    city: optionalText(120),
    language: z.enum(languages).nullable(),
  })
  .strict();
export type PatientSnapshot = z.infer<typeof patientSnapshotSchema>;

export interface LegalMentionsSnapshot {
  readonly primaryText: string;
  readonly taxNote: string;
  readonly vatMention: { readonly kind: string; readonly text: string };
  readonly identifiers: readonly {
    readonly kind: string;
    readonly label: string;
    readonly value: string;
  }[];
  readonly generalMentions: readonly string[];
  readonly customMentions: readonly string[];
  readonly warnings: readonly { readonly code: string; readonly messageKey: string }[];
  readonly isB2B: boolean;
}

export interface InvoiceDto {
  id: string;
  patientId: string;
  encounterId: string | null;
  practitionerMemberId: string;
  locationId: string | null;
  /** Nul tant que la facture est un brouillon : un brouillon ne consomme pas de numéro. */
  number: string | null;
  year: number | null;
  sequenceNumber: number | null;
  status: InvoiceStatus;
  vatMode: VatMode | null;
  issueDate: string;
  dueDate: string | null;
  subtotalCents: number;
  vatCents: number;
  totalCents: number;
  paidCents: number;
  creditedCents: number;
  /** Reste dû = total − encaissé − avoirs appliqués. Calculé, jamais stocké. */
  outstandingCents: number;
  legalMentions: LegalMentionsSnapshot | null;
  /**
   * Identité du client figée à l'émission. Nulle sur un brouillon, et nulle sur une facture
   * émise avant l'instantané : l'écran lit alors le dossier vivant.
   */
  patientSnapshot: PatientSnapshot | null;
  /** Impression PDF rangée au dossier du patient. Nulle tant que la facture n'a pas été imprimée. */
  documentId: string | null;
  notes: string | null;
  cancelledAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreditNoteDto {
  id: string;
  invoiceId: string;
  number: string | null;
  year: number | null;
  sequenceNumber: number | null;
  status: CreditNoteStatus;
  reason: CreditNoteReason;
  issueDate: string;
  totalCents: number;
  appliedCents: number;
  refundDueCents: number;
  legalMentions: LegalMentionsSnapshot | null;
  /** Impression PDF rangée au dossier du patient. Posée à l'émission de l'avoir. */
  documentId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreditNoteDetailDto extends CreditNoteDto {
  lines: InvoiceLineDto[];
}
